import { useEffect, useRef, type PropsWithChildren } from 'react';
import { Spin } from 'antd';
import { buildDeniedUrl, navigateExternal } from '@bettercomp/ams-spa-auth';
import { useAuthContext } from './AuthContext';
import { RetryableError } from './RetryableError';
import { SignedOut } from './SignedOut';

export interface GateViewProps {
  /**
   * Deep link restored after the hosted-UI login round-trip. The host chooses its
   * shape: the AMS SPA shells pass location.pathname only (any query is dropped);
   * a host may pass path plus query instead (e.g. Next.js router.asPath).
   */
  currentPath: string;
  /** Replace-style client-side navigation. Must be referentially stable across renders. */
  navigateReplace: (path: string) => void;
  /**
   * Replaces the expired-session reaction: called with currentPath instead of
   * session.beginLogin, for hosts whose login redirect is not the package's
   * hosted-UI leg (e.g. a host that decides the auth flow per company at its
   * callback). Must navigate away, and must be referentially stable across
   * renders.
   */
  onSessionExpired?: (currentPath: string) => void;
}

export function GateView({ currentPath, navigateReplace, onSessionExpired, children }: PropsWithChildren<GateViewProps>) {
  const { outcome, configError, devBypass, session, reportOutcome, retry } = useAuthContext();

  // One login redirect per expiry: a currentPath identity change while the
  // redirect is in flight must not mint a second PKCE verifier (the store is
  // last-write-wins, which would detach the first redirect's challenge).
  const loginRedirectStartedRef = useRef(false);
  useEffect(() => {
    if (!outcome) {
      return;
    }
    if (outcome.kind === 'sessionExpired' && !loginRedirectStartedRef.current) {
      loginRedirectStartedRef.current = true;
      if (onSessionExpired) {
        onSessionExpired(currentPath);
      } else if (session) {
        void session.beginLogin(currentPath).then((result) => {
          if (result.kind === 'redirect') {
            navigateExternal(result.url);
            return;
          }
          // No redirect happened: re-arm so a retried bootstrap that expires
          // again can start a fresh login.
          loginRedirectStartedRef.current = false;
          reportOutcome(result);
        });
      }
    }
    if (outcome.kind === 'terminal') {
      navigateReplace(buildDeniedUrl(outcome));
    }
  }, [outcome, session, currentPath, navigateReplace, onSessionExpired, reportOutcome]);

  if (devBypass) {
    return <>{children}</>;
  }

  if (!outcome) {
    if (configError) {
      return <RetryableError onRetry={retry} />;
    }
    return <Spin data-testid="auth-gate-loading" />;
  }

  if (outcome.kind === 'minted') {
    return <>{children}</>;
  }

  if (outcome.kind === 'signedOut') {
    return <SignedOut />;
  }

  if (outcome.kind === 'unavailable') {
    return <RetryableError onRetry={retry} />;
  }

  return null;
}
